"use client";

import { FC, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import classNames from "classnames";

import { IconType, OfficeRecord } from "@types";

import { Button } from "components/atoms/Button/Button";

import { SLUGS } from "consts";
import {
  useOfficeContactPrefillLocation,
  useOfficeContactPrefillQuery,
} from "context";
import { CBButton } from "components/InlineBlocks/CBButton";
import { OfficeLocationsCarousel } from "components/InlineBlocks/OfficeLocationsCarousel";
import { appendOfficeContactQueryToHref } from "utils/officeContact";
import { getContactPrefillOfficeSlug } from "utils/officePath";
import { matchOfficeLocationBySlug } from "utils/officeLocation";
import s from "./OfficeInfo.module.scss";
import { StructuredText } from "components/sections/RichtextSection/StructuredText";

interface Props {
  office: OfficeRecord;
}

export const OfficeInfo: FC<Props> = ({ office }) => {
  const t = useTranslations("common");
  const contactPrefill = useOfficeContactPrefillQuery();
  const contactPrefillOfficeSlug =
    contactPrefill?.officeSlug ?? getContactPrefillOfficeSlug(office);
  const { activeLocationSlug, setActiveLocationSlug } = useOfficeContactPrefillLocation();
  const [locationInitialized, setLocationInitialized] = useState(false);
  const locationCount = office.officeLocations?.length ?? 0;

  useEffect(() => {
    if (locationInitialized || !office.officeLocations?.length) {
      return;
    }

    const hashSlug = window.location.hash?.split("#")[1];
    const matchedLocation = hashSlug
      ? matchOfficeLocationBySlug(office.officeLocations, hashSlug)
      : undefined;

    if (matchedLocation) {
      setActiveLocationSlug(matchedLocation.slug);
    } else if (office.officeLocations.length > 1) {
      setActiveLocationSlug(office.officeLocations[0].slug);
    }

    setLocationInitialized(true);
  }, [locationInitialized, office.officeLocations, setActiveLocationSlug]);

  const contactLinkHref = useMemo(() => {
    const prefill = contactPrefill ?? {
      officeSlug: contactPrefillOfficeSlug,
      locationSlug: activeLocationSlug,
      locationCount,
    };

    return appendOfficeContactQueryToHref(SLUGS.contactGeneral, prefill);
  }, [activeLocationSlug, contactPrefill, contactPrefillOfficeSlug, locationCount]);

  const isSubpage = !!office.parent;
  const carouselActiveLocation = activeLocationSlug ?? office.officeLocations?.[0]?.slug ?? "";

  return (
    <div className={classNames(s.OfficeInfo, { [s.IsSubpage]: isSubpage })}>
      <div className={s.Inner}>
        {office.subtitle && <h2>{office.subtitle}</h2>}
        <div className={s.Text}>
          <StructuredText structuredText={office.intro as any} />
        </div>
        {!office.parent && (
          <Button
            label={t("contact-us")}
            link={{ href: contactLinkHref }}
            theme="dark"
            icon={IconType.Email}
            preIcon
            iconSize="m"
          />
        )}
        {office.callToActions?.length > 0 && (
          <div className={s.Buttons}>
            {office.callToActions?.map((callToAction) => (
              <div key={callToAction.id}>
                <CBButton {...callToAction} />
              </div>
            ))}
          </div>
        )}
      </div>
      {!isSubpage && office.officeLocations && (
        <div className={s.OfficeLocations}>
          <OfficeLocationsCarousel
            officeLocations={office.officeLocations}
            activeLocation={carouselActiveLocation}
          />
        </div>
      )}
    </div>
  );
};
